import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  automationNodesService,
  AutomationNode,
  CreateAutomationNodeData,
  UpdateAutomationNodeData,
} from '@/services/automation-nodes.service';
import { toast } from 'sonner';

export const automationNodeKeys = {
  all: ['automation-nodes'] as const,
  lists: () => [...automationNodeKeys.all, 'list'] as const,
  list: (productId: string) => [...automationNodeKeys.lists(), productId] as const,
  details: () => [...automationNodeKeys.all, 'detail'] as const,
  detail: (productId: string, nodeId: string) => [...automationNodeKeys.details(), productId, nodeId] as const,
};

export function useAutomationNodes(productId: string) {
  return useQuery<AutomationNode[]>({
    queryKey: automationNodeKeys.list(productId),
    queryFn: () => automationNodesService.getProductNodes(productId),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useCreateAutomationNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: CreateAutomationNodeData }) =>
      automationNodesService.createNode(productId, data),
    onSuccess: (node, { productId }) => {
      queryClient.invalidateQueries({ queryKey: automationNodeKeys.list(productId) });
      queryClient.setQueryData(automationNodeKeys.detail(productId, node.id), node);
      toast.success('Automation node created successfully');
    },
    onError: () => {
      toast.error('Failed to create automation node');
    },
  });
}

export function useUpdateAutomationNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      nodeId,
      data,
    }: {
      productId: string;
      nodeId: string;
      data: UpdateAutomationNodeData;
    }) => automationNodesService.updateNode(productId, nodeId, data),
    onSuccess: (node, { productId, nodeId }) => {
      queryClient.setQueryData(automationNodeKeys.detail(productId, nodeId), node);
      queryClient.invalidateQueries({ queryKey: automationNodeKeys.list(productId) });
      toast.success('Automation node updated successfully');
    },
    onError: () => {
      toast.error('Failed to update automation node');
    },
  });
}

export function useDeleteAutomationNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, nodeId }: { productId: string; nodeId: string }) =>
      automationNodesService.deleteNode(productId, nodeId),
    onSuccess: (_, { productId, nodeId }) => {
      queryClient.removeQueries({ queryKey: automationNodeKeys.detail(productId, nodeId) });
      queryClient.invalidateQueries({ queryKey: automationNodeKeys.list(productId) });
      toast.success('Automation node deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete automation node');
    },
  });
}
